<template>
  <div class="ui-radiocards">
    <div
      v-for="(card, i) in cards"
      :key="i"
      :class="[
        'ui-radiocards__card',
        {'ui-radiocards__card_active': card.active || card.value === value },
        {'ui-radiocards__card_disabled': card.disabled },
      ]"
      @click="cardClick(card)"
    >
      <div v-if="card.icon" class="ui-radiocards__icon">
        <f-sprite-icon :icon-link="card.icon" />
      </div>

      <div class="ui-radiocards__card-inner">
        <div v-text="card.title" class="ui-radiocards__title" />

        <div v-text="card.subtitle" class="ui-radiocards__subtitle" />

        <div class="ui-radiocards__content">
          <slot :value="card.value" />
        </div>

        <div class="ui-radiocards__action">
          <ui-button
            v-if="card.actionText && card.actionClick"
            :text="card.actionText"
            flat
            @click="card.actionClick"
          />
          <div
            v-else-if="showRadio"
            class="ui-radiocards__radio"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ui-radiocards',

  props: {
    value: {
      type: [String, Number],
      default: '',
    },

    cards: {
      type: Array,
      default: () => [],
    },

    showRadio: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    cardClick(card) {
      this.$emit('input', card.value);
      this.$emit('change', card.value);
    },
  },
};
</script>

<style lang="scss">
.ui-radiocards {
  background: $gray_bgr;
  margin: 12px (-$containerGapMobile);
  padding: $containerGapMobile;

  &__card {
    cursor: pointer;
    display: flex;
    background: $white;
    border-radius: 4px;
    padding: 12px;
    box-shadow: inset 0 0 0 1px $gray_lu, 0 2px 8px rgba(0, 0, 0, .05),
    0 8px 24px rgba(0, 0, 0, .03),
    0 16px 32px rgba(0, 0, 0, .02);
    transition: box-shadow .2s;

    &:not(:first-child) {
      margin-top: 12px;
    }

    &_active {
      box-shadow: inset 0 0 0 2px $blue,
      0 8px 24px rgba(0, 0, 0, .03),
      0 16px 32px rgba(0, 0, 0, .02);

      .ui-radiocards__radio {
        box-shadow: inset 0 0 0 7px $blue;
      }
    }

    &_disabled {
      box-shadow: inset 0 0 0 1px $gray_lu;
      background: $gray_bgr;
      pointer-events: none;

      .ui-radiocards__title,
      .ui-radiocards__icon,
      .ui-radiocards__subtitle {
        color: $gray_l2;
      }

      .ui-radiocards__icon {
        background-color: $gray_lu2;
      }

      .ui-radiocards__content img {
        filter: grayscale(100%);
        -webkit-filter: grayscale(100%);
        -moz-filter: grayscale(100%);
        opacity: .3;
      }
    }
  }

  &__card-inner {
    flex: 1;
    display: grid;
    grid-template-columns: auto 36px;
    grid-auto-rows: min-content;
  }

  &__icon {
    flex-basis: 36px;
    min-width: 36px;
    height: 36px;
    margin-right: 12px;
    border-radius: 50%;
    padding: 9px;
    background: $gray_bgr;
    color: $gray;
  }

  &__title {
    color: $black;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 4px;
    grid-column: 1 / 2;
    grid-row: 1 / 1;
  }

  &__subtitle {
    color: $gray;
    font-size: 12px;
    line-height: 16px;
    grid-column: 1 / 2;
    grid-row: 2 / 2;
  }

  &__action {
    min-width: 36px;
    margin-left: auto;
    position: relative;
  }

  &__radio {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    position: absolute;
    top: 0;
    right: 0;
    box-shadow: inset 0 0 0 1px $gray_lu;
    transition: box-shadow .2s .1s;
  }

  &__content {
    grid-column: 1 / 3;
    grid-row: 3 / 3;
  }
}

@include breakpoint("tablet") {
  .ui-radiocards {
    margin: 16px 0;
    padding: 16px;
    border-radius: 4px;

    &__card {
      flex: 1;
      min-height: 96px;

      &_disabled {
        cursor: default;
      }

      &:not(:first-child) {
        margin: 16px 0 0 0;
      }

      &:not(.active):hover {
        box-shadow: inset 0 0 0 1px $gray_lu, 0 3px 12px rgba(0, 0, 0, .08);
      }
    }
  }
}

@include breakpoint("desktop") {
  .ui-radiocards {
    display: flex;
    padding: 24px;

    &__card {
      padding: 16px 16px 12px;

      &:not(:first-child) {
        margin: 0 0 0 24px;
      }
    }

    &__icon {
      flex-basis: 48px;
      min-width: 48px;
      height: 48px;
      padding: 12px;
    }

    &__title {
      font-size: 16px;
      line-height: 24px;
    }

    &__subtitle {
      font-size: 14px;
      line-height: 16px;
    }
  }
}
</style>
